import { useCallback, useId } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Group from 'components/utils/Group'
import Typography from 'LuxKit/Typography/Typography'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from '../FormatCurrency'
import type { FilterPanelCheckItem } from './FilterPanelCheckboxGroup.types'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { useIntl } from 'react-intl'
import { rem } from 'polished'

export type FilterPanelCheckboxDensity = 'default' | 'compact'

/**
 * LuxKit AssistChip for the Figma “Only” action. Absolutely positioned on the
 * row’s trailing edge so it can sit over the price without sharing a flex track.
 */
const OnlyChip = styled(AssistChip)`
  position: absolute;
  inset-inline-end: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  opacity: 0;
  pointer-events: none;
`

const ItemRow = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: ${rem(32)};
  padding-block: ${rem(4)};
  box-sizing: border-box;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};

  /* Inherit from the row so LuxKit checkbox/chip cursor styles stay untouched. */
  & * {
    cursor: inherit;
  }

  &:hover ${OnlyChip} {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover .trailing.has-only {
    opacity: 0;
  }
`

/** Checkbox + label (+ optional trailing price/count). Matches Figma LHS / RHS columns. */
const RowLabel = styled.label<{ $hideCheckbox?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$hideCheckbox ? 0 : rem(8)};
  flex: 1;
  min-width: 0;
  height: 100%;
`

const ItemLabel = styled(BodyText)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Trailing = styled.span`
  flex-shrink: 0;
  margin-inline-start: ${rem(16)};
  transition: opacity 0.15s ease;
`

const LuxPlusLabel = styled.span`
  color: ${props => props.theme.palette.product.luxPlus.foreground};
`

interface Props {
  name: string
  item: FilterPanelCheckItem
  defaultChecked?: boolean
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  titlecase?: boolean
  maxValue?: number
  loadingValue?: boolean
  hideCheckbox?: boolean
  /**
   * `compact` is the flights redesign layout. Default keeps the legacy
   * CheckboxInput row used by hotels, cruises, tours, experiences, etc.
   */
  density?: FilterPanelCheckboxDensity
  showOnly?: boolean
  onOnlyClick?: (value: string | number) => void
  luxPlusLabel?: boolean
  highlightLowestPrice?: boolean
  lowestPrice?: number
}

function FilterPanelCheckboxGroupItem(props: Props) {
  const {
    onChange,
    name,
    item,
    defaultChecked,
    checked,
    titlecase = false,
    maxValue = 1000,
    loadingValue,
    hideCheckbox = false,
    density = 'default',
    showOnly = false,
    onOnlyClick,
    luxPlusLabel = false,
    highlightLowestPrice = false,
    lowestPrice,
  } = props

  const intl = useIntl()
  const inputId = useId()

  const showLabelOnly = item.price === undefined && (typeof item.count !== 'number' || item.hideCount)
  const showCount = typeof item.count === 'number' && item.price === undefined && !item.hideCount
  const showPrice = item.price !== undefined
  // Checkout intentionally hides checklist prices (`hidePrice`); standalone keeps Only on priced rows only.
  const canShowOnly = density === 'compact' && showOnly && !!onOnlyClick && (showPrice || !!item.hidePrice)
  const isLowestPrice = highlightLowestPrice &&
    lowestPrice !== undefined &&
    item.price === lowestPrice

  const exceedsMaxCountLabel = intl.formatMessage(
    {
      id: 'fhC-ma__babrGtmtNhVx',
      defaultMessage: '({maxCount}+)',
      description: 'Filter checkbox item count when it exceeds the maximum displayable value',
    },
    { maxCount: maxValue },
  )
  const countLabel = intl.formatMessage(
    {
      id: 'AQj1DTTPdmt-bGxPXG7x',
      defaultMessage: '({count})',
      description: 'Parenthesised count of matching results shown next to a filter checkbox option',
    },
    { count: item.count },
  )
  const onlyLabel = intl.formatMessage({
    id: 'F2VUOh24QI68H9ECqZqn',
    defaultMessage: 'Only',
    description: 'Button label to filter results to a single airline or stopover city option',
  })

  const handleOnlyClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onOnlyClick?.(item.value)
    ;(e.currentTarget as HTMLElement).blur()
  }, [item.value, onOnlyClick])

  if (density === 'default') {
    return (
      <CheckboxInput
        key={item.value}
        name={name}
        value={item.value}
        size="medium"
        onChange={onChange}
        defaultChecked={defaultChecked}
        checked={item.checked ?? checked}
        disabled={item.disabled}
        hidden={item.hidden}
        data-carrier={item.value}
        hideCheckbox={hideCheckbox}
      >
        {showCount && (
          <Group direction="horizontal" horizontalAlign="space-between">
            {item.label}
            {loadingValue && <TextLoadingBox width="3ch" />}
            {!loadingValue && <span>{item.count! > maxValue ? exceedsMaxCountLabel : countLabel}</span>}
          </Group>
        )}

        {showPrice && (
          <Group direction="horizontal" horizontalAlign="space-between" gap="0 4">
            {item.label}

            <span>
              <FormatCurrency
                value={item.price!}
                format="roundedDollar"
              />
            </span>
          </Group>
        )}

        {showLabelOnly && (
          <Typography format={titlecase ? 'titlecase' : undefined} as="span">
            {item.label}
          </Typography>
        )}
      </CheckboxInput>
    )
  }

  const nameContent = luxPlusLabel ? (
    <LuxPlusLabel>{item.label}</LuxPlusLabel>
  ) : titlecase ? (
    <Typography format="titlecase" as="span">{item.label}</Typography>
  ) : (
    item.label
  )

  const priceColour = luxPlusLabel
    ? 'lux-plus'
    : isLowestPrice
      ? 'highlight-primary-dark'
      : undefined

  const priceContent = (
    <BodyText variant="medium" colour={priceColour}>
      <FormatCurrency
        value={item.price!}
        format="roundedDollar"
      />
    </BodyText>
  )

  return (
    <ItemRow $disabled={item.disabled}>
      <RowLabel htmlFor={inputId} $hideCheckbox={hideCheckbox}>
        <CheckboxButton
          id={inputId}
          name={name}
          value={item.value}
          size="medium"
          onChange={onChange}
          defaultChecked={defaultChecked}
          checked={item.checked ?? checked}
          disabled={item.disabled}
          hidden={item.hidden}
          data-carrier={item.value}
          hideCheckbox={hideCheckbox}
        />

        <ItemLabel
          variant="medium"
          colour="normal-contrast"
          forwardedAs="span"
        >
          {nameContent}
        </ItemLabel>

        {showCount && (
          <Trailing>
            {loadingValue && <TextLoadingBox width="3ch" />}
            {!loadingValue && <BodyText variant="medium" as="span">{item.count! > maxValue ? exceedsMaxCountLabel : countLabel}</BodyText>}
          </Trailing>
        )}

        {showPrice && (
          <Trailing className={canShowOnly ? 'trailing has-only' : 'trailing'}>
            {priceContent}
          </Trailing>
        )}
      </RowLabel>

      {canShowOnly && (
        <OnlyChip
          variant="filled"
          size="medium"
          onClick={handleOnlyClick}
          type="button"
        >
          {onlyLabel}
        </OnlyChip>
      )}
    </ItemRow>
  )
}

export default FilterPanelCheckboxGroupItem
